import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { CommonService } from 'src/app/shared/common.service';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { APIResponse } from 'src/app/core/models/api-response';
import { SetLoading } from 'src/app/shared/store/shared.actions';
import { AlertEnums } from 'src/app/shared/enums/shared.enums';
import * as catalogsActions from './catalogs.actions';
import { ICatalog } from '../models/catalogs.models';
import { CatalogsService } from '../services/catalogs.services';
import { ILocation } from '../../auth/models/auth-response';

@Injectable()
export class CatalogsEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private catalogsServices: CatalogsService,
    private commonService: CommonService
  ) {}

  // fetch All Catalogs
  FetchAllCatalogsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(catalogsActions.FetchAllCatalogs),
      switchMap(() =>
        this.catalogsServices.listAllCatalogs().pipe(
          map((res: APIResponse<ICatalog>) =>
            catalogsActions.FetchAllCatalogsSuccess({ catalogs: res })
          ),
          tap(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
          }),
          catchError(() =>
            of(catalogsActions.FetchAllCatalogsError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );

  // fetch all products
  FetchCatalogByIdEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(catalogsActions.FetchCatalogById),
      switchMap((action) =>
        this.catalogsServices.listCatalogById(action.id).pipe(
          map((res: APIResponse<ICatalog>) =>
            catalogsActions.FetchCatalogByIdSuccess({ catalog: res.object! })
          ),
          tap(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
          }),
          catchError(() =>
            of(catalogsActions.FetchCatalogByIdError()).pipe(
              tap(() => {
                this.store.dispatch(SetLoading({ isAppLoading: false }));
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );

  // get locations by company id
  GetLocationsByCompanyIdEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(catalogsActions.GetLocationsByCompanyId),
      tap(() => {
        this.store.dispatch(SetLoading({ isAppLoading: true }));
      }),
      switchMap((action) =>
        this.catalogsServices.getLocationsByCompanyId(action.id).pipe(
          map((res: APIResponse<ILocation>) =>
            catalogsActions.GetLocationsByCompanyIdSuccess({ locations: res })
          ),
          catchError(() => of(catalogsActions.GetLocationsByCompanyIdError())),
          finalize(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
          })
        )
      )
    )
  );

  loadCatalogs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(catalogsActions.LoadCatalogs),
      tap(() => this.store.dispatch(catalogsActions.Loading({ loading: true }))),
      switchMap((action) =>
        this.catalogsServices.listAllCatalogs(action.page, action.key).pipe(
          map((resp) => catalogsActions.LoadCatalogsSuccess({ resp })),
          catchError((error) => of(catalogsActions.LoadCatalogsError({ error: error.message }))),
          finalize(() => this.store.dispatch(catalogsActions.Loading({ loading: false })))
        )
      )
    );
  });

  appendCatalogs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(catalogsActions.AppendCatalogs),
      switchMap((action) =>
        this.catalogsServices.listAllCatalogs(action.page, action.key).pipe(
          map((resp) => catalogsActions.AppendCatalogsSuccess({ resp })),
          catchError((error) => of(catalogsActions.AppendCatalogsError({ error: error.message })))
        )
      )
    );
  });

  loadProducts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(catalogsActions.LoadProducts),
      tap(() => this.store.dispatch(catalogsActions.Loading({ loading: true }))),
      switchMap((action) =>
        this.catalogsServices.listCatalogById(action.id, action.page, action.key, action.filter, action.sort_by, action.sort_mode).pipe(
          map((resp) => catalogsActions.LoadProductsSuccess({ resp })),
          catchError((error) => of(catalogsActions.LoadProductsError({ error: error.message }))),
          finalize(() => this.store.dispatch(catalogsActions.Loading({ loading: false })))
        )
      )
    );
  });

  appendProducts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(catalogsActions.AppendProducts),
      switchMap((action) =>
        this.catalogsServices.listCatalogById(action.id, action.page, action.key, action.filter, action.sort_by, action.sort_mode).pipe(
          map((resp) => catalogsActions.AppendProductsSuccess({ resp })),
          catchError((error) => of(catalogsActions.AppendProductsError({ error: error.message })))
        )
      )
    );
  });

  loadCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(catalogsActions.LoadCart),
      switchMap((action) =>
        this.catalogsServices.loadCart().pipe(
          map((resp) => {
            if (resp.data) {
              resp.data = resp.data.map(cart => ({
                ...cart,
                products: cart.products?.map(p => {
                  const handlingUnitId = (p as any).handling_unit_id;
                  const selectedHandlingUnitId = p.selected_handling_unit_id;
                  const finalSelectedId = handlingUnitId || selectedHandlingUnitId;
                  
                  // Filter handling units to only enabled ones
                  const filteredHandlingUnits = (p.handling_units || []).filter((hu: any) => hu.enabled !== false);
                  
                  return {
                    ...p,
                    // qte_selected est déjà en unités totales (total units) - pas de conversion nécessaire
                    selected_handling_unit_id: finalSelectedId,
                    handling_units: filteredHandlingUnits
                  };
                }) || []
              }));
            }
            
            return catalogsActions.LoadCartSuccess({ carts: resp.data });
          }),
          catchError((error) => of(catalogsActions.AddToCartFailure()))
        )
      )
    );
  });

  addToCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(catalogsActions.AddToCart),
      switchMap((action) =>
        this.catalogsServices.addToCart(action.catalog, action.product).pipe(
          map((resp) => {
            return catalogsActions.AddToCartSuccess({ cart: resp.object });
          }),
          catchError((error) => of(catalogsActions.AddToCartFailure()))
        )
      )
    );
  });

  batchCartUpdate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(catalogsActions.BatchCartUpdate),
      switchMap((action) =>
        this.catalogsServices.batchUpdateCart(action.catalog, action.products).pipe(
          map((resp) => {
            return catalogsActions.BatchCartUpdateSuccess({ cart: resp.object });
          }),
          catchError((error) => of(catalogsActions.BatchCartUpdateFailure()))
        )
      )
    );
  });

  updateCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(catalogsActions.UpdateCart),
      switchMap((action) =>
        this.catalogsServices.updateCart(action.cart).pipe(
          map((resp) => catalogsActions.UpdateCartSuccess({ cart: resp.object })),
          catchError((error) => of(catalogsActions.UpdateCartFailure()))
        )
      )
    );
  });

  deleteCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(catalogsActions.DeleteCart),
      switchMap((action) =>
        this.catalogsServices.deleteCart(action.cartId).pipe(
          map((resp) => catalogsActions.DeleteCartSuccess({ cart: resp.object })),
          catchError((error) => of(catalogsActions.DeleteCartFailure()))
        )
      )
    );
  });

  checkProductsAvailability$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(catalogsActions.CheckProductsAvailability),
      tap(() => this.store.dispatch(catalogsActions.Loading({ loading: true }))),
      switchMap((action) =>
        this.catalogsServices.checkProductAvailability(action.data).pipe(
          map((resp) => catalogsActions.CheckProducstAvailabilitySuccess({ data: resp.object })),
          catchError((error) => of(catalogsActions.CheckProductsAvailabilityFailure())),
          finalize(() => this.store.dispatch(catalogsActions.Loading({ loading: false })))
        )
      )
    );
  });
}
